import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  BardoContextDataService,
  EstabelecimentoDataService,
  ProfissionalService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { I18nService, MoedaPipe } from '@nomoh-monorepo/data-access-shared-i18n';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { IProfissional } from '@nomoh-monorepo/shared-types';
import {
  DeviceService,
  NMultiselectComponent,
} from '@nomoh-monorepo/shared-common-ui';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface AgendamentoFechamento {
  dataHora: string | Date;
  servicoId: string;
  servicoNome: string;
  valor: number;
}

interface FechamentoPorProfissional {
  profissionalId: string;
  profissionalNome: string;
  agendamentos: AgendamentoFechamento[];
  total: number;
}

interface OpcaoProfissional {
  label: string;
  value: string | null;
}

@Component({
  selector: 'lib-fechamento',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    SelectModule,
    NMultiselectComponent,
    TranslateModule,
    MoedaPipe,
  ],
  providers: [DatePipe],
  templateUrl: './fechamento.component.html',
  styleUrls: ['./fechamento.component.scss'],
})
export class FechamentoComponent implements OnInit {
  private contextoService = inject(BardoContextDataService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private profissionalService = inject(ProfissionalService);
  private notificacaoService = inject(NotificacaoService);
  private i18n = inject(I18nService);
  private datePipe = inject(DatePipe);
  private translateService = inject(TranslateService);
  private langChangeTrigger = toSignal(this.translateService.onLangChange);
  deviceService = inject(DeviceService);

  $perfilAtivo = this.contextoService.$perfilAtivo;
  $ehAdministrador = computed(() => this.$perfilAtivo() === 'ADMINISTRADOR');

  dataInicio = '';
  dataFim = '';

  profissionais = signal<IProfissional[]>([]);
  opcoesProfissional = computed<OpcaoProfissional[]>(() => {
    this.langChangeTrigger();
    return [
      {
        label: this.translateService.instant('BARDO_FECHAMENTO.FILTROS.TODOS_PROFISSIONAIS'),
        value: null,
      },
      ...this.profissionais().map((p) => ({ label: p.nome, value: p.id as string })),
    ];
  });
  private _profissionalSelecionadoId = signal<string | null>(null);
  get profissionalSelecionadoId(): string | null {
    return this._profissionalSelecionadoId();
  }
  set profissionalSelecionadoId(value: string | null) {
    this._profissionalSelecionadoId.set(value);
  }

  porProfissional = signal<FechamentoPorProfissional[]>([]);
  totalGeral = signal<number>(0);
  loading = signal<boolean>(false);
  $semResultados = computed(
    () => !this.porProfissional().some((grupo) => grupo.agendamentos.length)
  );

  $subtitulo = computed(() => {
    this.langChangeTrigger();
    if (!this.$ehAdministrador()) {
      return this.translateService.instant('BARDO_FECHAMENTO.SUBTITULO_PROFISSIONAL');
    }
    const profissionalId = this._profissionalSelecionadoId();
    if (profissionalId) {
      const nome = this.profissionais().find((p) => p.id === profissionalId)?.nome;
      return nome
        ? this.translateService.instant('BARDO_FECHAMENTO.SUBTITULO_ADMIN_PROFISSIONAL', { nome })
        : this.translateService.instant('BARDO_FECHAMENTO.SUBTITULO_ADMIN_SELECIONADO');
    }
    return this.translateService.instant('BARDO_FECHAMENTO.SUBTITULO_ADMIN_TODOS');
  });

  ngOnInit(): void {
    this.inicializarDatas();

    if (this.$ehAdministrador()) {
      this.carregarProfissionais();
    } else {
      this.profissionalSelecionadoId = this.contextoService.$profissionalIdVinculado();
    }

    this.buscarRelatorio();
  }

  private inicializarDatas() {
    const hoje = new Date();
    const inicioSemana = new Date(hoje);
    inicioSemana.setDate(hoje.getDate() - hoje.getDay()); // Domingo da semana atual

    this.dataInicio = this.formatarDataInput(inicioSemana);
    this.dataFim = this.formatarDataInput(hoje);
  }

  private formatarDataInput(data: Date): string {
    return data.toISOString().split('T')[0];
  }

  private async carregarProfissionais() {
    const estabelecimentoId = this.estabelecimentoDataService.$estabelecimentoAtual()?.id;
    if (!estabelecimentoId) return;

    try {
      const profissionais = await this.profissionalService.findAll({
        estabelecimentoId,
      });
      this.profissionais.set(profissionais);
    } catch (error) {
      console.error(error);
    }
  }

  async buscarRelatorio() {
    if (!this.dataInicio || !this.dataFim) {
      return;
    }

    if (new Date(this.dataInicio) > new Date(this.dataFim)) {
      return;
    }

    try {
      this.loading.set(true);

      if (this.profissionalSelecionadoId) {
        const { agendamentos, total } = await this.profissionalService.buscarFechamento(
          this.profissionalSelecionadoId,
          this.dataInicio,
          this.dataFim
        );
        const profissionalNome =
          this.profissionais().find((p) => p.id === this.profissionalSelecionadoId)?.nome ?? '';
        this.porProfissional.set([
          { profissionalId: this.profissionalSelecionadoId, profissionalNome, agendamentos, total },
        ]);
        this.totalGeral.set(total);
        return;
      }

      const estabelecimentoId = this.estabelecimentoDataService.$estabelecimentoAtual()?.id;
      if (!estabelecimentoId) return;

      const { porProfissional, totalGeral } = await this.profissionalService.buscarFechamentoGeral(
        estabelecimentoId,
        this.dataInicio,
        this.dataFim
      );
      this.porProfissional.set(porProfissional);
      this.totalGeral.set(totalGeral);
    } catch (error) {
      console.error(error);
      this.notificacaoService.notificacaoError(
        this.translateService.instant('BARDO_FECHAMENTO.NOTIFICACOES.ERRO_BUSCAR')
      );
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Abre o relatório de fechamento em uma nova aba, formatado só em preto e
   * branco (sem depender das cores da UI), e dispara a impressão do navegador.
   */
  imprimir(): void {
    const grupos = this.porProfissional().filter((grupo) => grupo.agendamentos.length);
    if (!grupos.length) return;

    const blob = new Blob([this.montarHtmlImpressao(grupos)], { type: 'text/html' });
    const url = URL.createObjectURL(blob);

    const janela = window.open(url, '_blank', 'noopener,noreferrer');
    if (!janela) {
      URL.revokeObjectURL(url);
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('BARDO_FECHAMENTO.NOTIFICACOES.PERMITIR_POPUPS')
      );
      return;
    }

    janela.onload = () => {
      janela.print();
      URL.revokeObjectURL(url);
    };
  }

  private montarHtmlImpressao(grupos: FechamentoPorProfissional[]): string {
    const periodo = `${this.datePipe.transform(this.dataInicio, 'dd/MM/yyyy')} a ${this.datePipe.transform(this.dataFim, 'dd/MM/yyyy')}`;
    const titulo = this.$ehAdministrador()
      ? this.translateService.instant('BARDO_FECHAMENTO.TITULO')
      : this.translateService.instant('BARDO_FECHAMENTO.MEU_FECHAMENTO');
    const colunaData = this.translateService.instant('BARDO_FECHAMENTO.RELATORIO.COLUNA_DATA');
    const colunaHora = this.translateService.instant('BARDO_FECHAMENTO.RELATORIO.COLUNA_HORA');
    const colunaServico = this.translateService.instant('BARDO_FECHAMENTO.RELATORIO.COLUNA_SERVICO');
    const colunaValor = this.translateService.instant('BARDO_FECHAMENTO.RELATORIO.COLUNA_VALOR');
    const textoPeriodo = this.translateService.instant('BARDO_FECHAMENTO.RELATORIO.PERIODO', { periodo });
    const totalPeriodo = this.translateService.instant('BARDO_FECHAMENTO.TOTAL_PERIODO');
    const lang = this.translateService.currentLang || 'pt-BR';

    const gruposHtml = grupos
      .map(
        (grupo) => `
          <section class="grupo">
            <div class="grupo-header">
              <h2>${this.escapeHtml(grupo.profissionalNome)}</h2>
              <span>${this.i18n.formatarMoeda(grupo.total)}</span>
            </div>
            <table>
              <thead>
                <tr><th>${colunaData}</th><th>${colunaHora}</th><th>${colunaServico}</th><th>${colunaValor}</th></tr>
              </thead>
              <tbody>
                ${grupo.agendamentos
                  .map(
                    (agendamento) => `
                      <tr>
                        <td>${this.datePipe.transform(agendamento.dataHora, 'dd/MM/yyyy')}</td>
                        <td>${this.datePipe.transform(agendamento.dataHora, 'HH:mm')}</td>
                        <td>${this.escapeHtml(agendamento.servicoNome)}</td>
                        <td>${this.i18n.formatarMoeda(agendamento.valor)}</td>
                      </tr>
                    `
                  )
                  .join('')}
              </tbody>
            </table>
          </section>
        `
      )
      .join('');

    return `<!doctype html>
<html lang="${lang}">
  <head>
    <meta charset="utf-8" />
    <title>${titulo}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; color: #000; padding: 32px; }
      h1 { font-size: 20px; margin: 0 0 4px; }
      .periodo { font-size: 13px; margin: 0 0 24px; color: #333; }
      .grupo { margin-bottom: 24px; page-break-inside: avoid; }
      .grupo-header { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 2px solid #000; padding-bottom: 4px; margin-bottom: 8px; }
      .grupo-header h2 { font-size: 15px; margin: 0; }
      .grupo-header span { font-size: 14px; font-weight: bold; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { border: 1px solid #000; padding: 6px 8px; text-align: left; }
      th { background: #eee; }
      td:last-child, th:last-child { text-align: right; }
      .total-geral { margin-top: 16px; display: flex; justify-content: space-between; border-top: 2px solid #000; padding-top: 8px; font-size: 16px; font-weight: bold; }
      @media print { body { padding: 0; } }
    </style>
  </head>
  <body>
    <h1>${titulo}</h1>
    <p class="periodo">${textoPeriodo}</p>
    ${gruposHtml}
    <div class="total-geral">
      <span>${totalPeriodo}</span>
      <span>${this.i18n.formatarMoeda(this.totalGeral())}</span>
    </div>
  </body>
</html>`;
  }

  private escapeHtml(valor: string): string {
    const div = document.createElement('div');
    div.textContent = valor;
    return div.innerHTML;
  }
}
